const toTimestamp = (value) => new Date(value).getTime()

/**
 * A video availability interval on the timeline.
 *
 * @typedef {Object} CanvasFileRange
 * @property {string} startTime - Parseable date string representing the interval start.
 * @property {string} endTime - Parseable date string representing the interval end.
 */

/**
 * A buffered section intersecting a visible file range.
 * Coordinates are relative to the left edge of the viewport canvas.
 *
 * @typedef {Object} CanvasBufferedRange
 * @property {number} x - Horizontal start position in CSS pixels.
 * @property {number} width - Width of the buffered section in CSS pixels.
 */

/**
 * A file range prepared for drawing on the viewport canvas.
 *
 * @typedef {Object} CanvasBarRange
 * @property {number} x - Horizontal start position relative to the viewport canvas, in CSS pixels.
 * @property {number} width - Visible width of the file range in CSS pixels.
 * @property {CanvasBufferedRange|null} buffered - Visible buffered intersection, if one exists.
 */

/**
 * Options used to translate timeline dates into viewport canvas coordinates.
 *
 * @typedef {Object} GetCanvasBarRangesOptions
 * @property {CanvasFileRange[]} filelist - Video intervals to process.
 * @property {string} periodStart - Parseable date string for the full timeline start.
 * @property {string} periodEnd - Parseable date string for the full timeline end.
 * @property {number} timelineWidth - Width of the complete zoomed timeline in CSS pixels.
 * @property {number} viewportStart - Scroll offset from the timeline's left edge in CSS pixels.
 * @property {number} viewportWidth - Width of the visible canvas viewport in CSS pixels.
 * @property {number} minimumVisibleWidthPx - Minimum rendered width for sub-pixel file ranges.
 * @property {string} bufferedStart - Parseable date string for the buffered interval start.
 * @property {string} bufferedEnd - Parseable date string for the buffered interval end.
 */

/**
 * Converts video file intervals into rectangles that can be drawn on a viewport-sized canvas.
 *
 * File ranges are first clipped to `periodStart` and `periodEnd`, then translated from dates to
 * absolute positions on the complete zoomed timeline. Ranges outside the current viewport are
 * omitted. Returned coordinates are shifted by `viewportStart`, so `x = 0` represents the left
 * edge of the visible canvas rather than the left edge of the complete timeline.
 *
 * File ranges narrower than `minimumVisibleWidthPx` are visually enlarged without changing their
 * actual start or end dates. When a buffered interval intersects a visible file range, `buffered`
 * contains the clipped rectangle that should be drawn over the base availability rectangle.
 *
 * @param {GetCanvasBarRangesOptions} options
 * @returns {CanvasBarRange[]} Visible rectangles in the same order as the supplied filelist.
 *
 * @example
 * getCanvasBarRanges({
 *   filelist: [{ startTime: '2026-09-01T01:00:00Z', endTime: '2026-09-01T01:05:00Z' }],
 *   periodStart: '2026-09-01T00:00:00Z',
 *   periodEnd: '2026-09-01T12:00:00Z',
 *   timelineWidth: 43200,
 *   viewportStart: 3400,
 *   viewportWidth: 1000,
 *   minimumVisibleWidthPx: 2,
 *   bufferedStart: '2026-09-01T01:01:00Z',
 *   bufferedEnd: '2026-09-01T01:03:00Z',
 * })
 * // [{ x: 200, width: 300, buffered: { x: 260, width: 120 } }]
 */

export const getCanvasBarRanges = ({
  filelist,
  periodStart,
  periodEnd,
  timelineWidth,
  viewportStart,
  viewportWidth,
  minimumVisibleWidthPx,
  bufferedStart,
  bufferedEnd,
}) => {
  const periodStartTimestamp = toTimestamp(periodStart)
  const periodEndTimestamp = toTimestamp(periodEnd)
  const duration = periodEndTimestamp - periodStartTimestamp
  const viewportEnd = viewportStart + viewportWidth
  const bufferedStartTimestamp = toTimestamp(bufferedStart)
  const bufferedEndTimestamp = toTimestamp(bufferedEnd)

  if (
    !Array.isArray(filelist) ||
    !Number.isFinite(duration) ||
    duration <= 0 ||
    !Number.isFinite(timelineWidth) ||
    timelineWidth <= 0 ||
    !Number.isFinite(viewportWidth) ||
    viewportWidth <= 0
  ) {
    return []
  }

  const timestampToPixel = (timestamp) =>
    ((timestamp - periodStartTimestamp) / duration) * timelineWidth

  return filelist.reduce((result, { startTime, endTime }) => {
    const startTimestamp = Math.max(toTimestamp(startTime), periodStartTimestamp)
    const endTimestamp = Math.min(toTimestamp(endTime), periodEndTimestamp)

    if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp)) return result
    if (startTimestamp >= endTimestamp) return result

    const absoluteStart = timestampToPixel(startTimestamp)
    const absoluteEnd = timestampToPixel(endTimestamp)
    const renderedEnd = absoluteStart + Math.max(absoluteEnd - absoluteStart, minimumVisibleWidthPx)

    if (renderedEnd < viewportStart || absoluteStart > viewportEnd) return result

    const visibleStart = Math.max(absoluteStart, viewportStart)
    const visibleEnd = Math.min(renderedEnd, viewportEnd)
    const range = {
      x: visibleStart - viewportStart,
      width: visibleEnd - visibleStart,
      buffered: null,
    }

    if (
      Number.isFinite(bufferedStartTimestamp) &&
      Number.isFinite(bufferedEndTimestamp) &&
      bufferedStartTimestamp < bufferedEndTimestamp
    ) {
      const intersectionStart = Math.max(startTimestamp, bufferedStartTimestamp)
      const intersectionEnd = Math.min(endTimestamp, bufferedEndTimestamp)

      if (intersectionStart < intersectionEnd) {
        const bufferedAbsoluteStart = timestampToPixel(intersectionStart)
        const bufferedAbsoluteEnd = timestampToPixel(intersectionEnd)
        const bufferedVisibleStart = Math.max(bufferedAbsoluteStart, viewportStart)
        const bufferedVisibleEnd = Math.min(bufferedAbsoluteEnd, viewportEnd)

        if (bufferedVisibleStart < bufferedVisibleEnd) {
          range.buffered = {
            x: bufferedVisibleStart - viewportStart,
            width: bufferedVisibleEnd - bufferedVisibleStart,
          }
        }
      }
    }

    result.push(range)
    return result
  }, [])
}
